import { cn } from '@/lib/utils';
import { CoachmarkTargetProps } from './types';
import { useCoachmarkContext } from './context';
import { useLayoutEffect, useRef } from 'react';

const CoachmarkTarget = (props: CoachmarkTargetProps) => {
  const {
    children,
    className,
    targetId
  } = props;

  const targetRef = useRef<HTMLDivElement | null>(null);
  const {
    activeTargetHighlightMode,
    activeTargetId,
    isActiveTargetInteractive,
    registerTarget,
    unregisterTarget
  } = useCoachmarkContext();
  const isActive = activeTargetId === targetId;
  const isElevated = isActive && activeTargetHighlightMode === 'elevate';
  const isReplicated = isActive && activeTargetHighlightMode === 'replica';
  const isInteractionBlocked = isActive && (!isActiveTargetInteractive || isReplicated);

  useLayoutEffect(() => {
    const element = targetRef.current;

    if (!element) {
      return;
    }

    registerTarget(targetId, element);

    return () => {
      unregisterTarget(targetId, element);
    };
  }, [registerTarget, targetId, unregisterTarget]);

  useLayoutEffect(() => {
    const element = targetRef.current;

    if (!element || !isReplicated) return;

    element.setAttribute('inert', '');

    return () => {
      element.removeAttribute('inert');
    };
  }, [isReplicated]);

  return (
    <div
      className={cn('relative', className, isElevated && 'z-[70]', isInteractionBlocked && 'pointer-events-none')}
      aria-hidden={isReplicated ? true : undefined}
      data-coachmark-target-active={isActive ? 'true' : undefined}
      data-coachmark-target-id={targetId}
      ref={targetRef}
    >
      {children}
    </div>
  );
};

export default CoachmarkTarget;